/**
 * /backgammon — one match: games, turns, the doubling cube, the score.
 * Pure (no DOM, no clock, no randomness): dice come in from outside, so the
 * page, the online peer and the tests drive the same code.
 *
 * Phases of a game:
 *   opening  — each side throws one die; the higher starts and plays BOTH
 *              numbers as his first roll, a tie is thrown again [MS 26.3]
 *   roll     — the player on turn may offer the cube, then rolls
 *   double   — a cube offer waits for take / drop
 *   move     — dice are on the board; the turn is committed as a whole
 *   over     — result is known
 *
 * Crawford rule [MS 23.14]: the game right after a player first reaches
 * «match − 1» is played without the cube; later games have it again.
 */
import * as R from './rules.js';

export const MATCH_LENGTHS = [1, 3, 5, 7, 11, 0];   // 0 = no limit (a session)

/** PeerJS id prefix of a room (online.js), read by the page to recognise its own room after a reload. */
export const ROOM_PREFIX = 'bgm-';

export function defaultConfig(v) {
    return { variant: v === R.SHORT ? R.SHORT : R.LONG, matchTo: 3, cube: false, crawford: true, koksLong: false };
}

function emptyStats() {
    return { faces: [[0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0]], doubles: [0, 0], rolls: [0, 0], pips: [0, 0] };
}

export function newMatch(cfg) {
    const c = Object.assign(defaultConfig(cfg && cfg.variant), cfg || {});
    const m = {
        cfg: c,
        score: [0, 0],
        gameNo: 0,
        crawfordUsed: false,
        over: false,
        winner: -1,
        stats: emptyStats(),
        log: [],        // finished games: {winner, points, kind, cube}
        game: null,
    };
    m.game = newGame(m);
    return m;
}

export function isCrawfordGame(m) {
    return !!(m.game && m.game.crawford);
}

export function newGame(m, prev) {
    const cfg = m.cfg;
    let crawford = false;
    if (cfg.matchTo > 1 && cfg.cube && cfg.crawford && !m.crawfordUsed) {
        const t = cfg.matchTo - 1;
        if ((m.score[0] === t && m.score[1] < t) || (m.score[1] === t && m.score[0] < t)) {
            crawford = true;
            m.crawfordUsed = true;
        }
    }
    m.gameNo++;
    return {
        no: m.gameNo,
        v: cfg.variant,
        pos: R.initialPosition(cfg.variant),
        phase: 'opening',
        turn: -1,
        opening: [],                 // [[w,b], …] opening throws (ties included)
        dice: null,
        turnsPlayed: [0, 0],
        cube: { value: 1, owner: -1, offeredBy: -1 },
        crawford,
        history: [],                 // {p, dice, moves, note} | {p, kind:'double'|'take'|'drop'|'resign'}
        stats: emptyStats(),
        result: null,
        prevWinner: prev != null ? prev : -1,
    };
}

function tallyRoll(m, g, p, d1, d2) {
    for (const s of [m.stats, g.stats]) {
        s.faces[p][d1]++; s.faces[p][d2]++;
        s.rolls[p]++;
        if (d1 === d2) { s.doubles[p]++; s.pips[p] += 4 * d1; }
        else s.pips[p] += d1 + d2;
    }
}

/** The opening throw: White's die, Black's die. Returns true when the game starts. */
export function openingRoll(m, dW, dB) {
    const g = m.game;
    if (g.phase !== 'opening') return false;
    g.opening.push([dW, dB]);
    if (dW === dB) return false;
    const p = dW > dB ? R.WHITE : R.BLACK;
    g.turn = p;
    g.dice = p === R.WHITE ? [dW, dB] : [dB, dW];
    g.phase = 'move';
    // the opening throw is the starter's first roll; count it once for him
    tallyRoll(m, g, p, g.dice[0], g.dice[1]);
    return true;
}

export function firstTurnOf(g, p) {
    return g.turnsPlayed[p] === 0;
}

export function cubeAvailable(m) {
    const cfg = m.cfg;
    return !!cfg.cube && cfg.matchTo !== 1 && !m.game.crawford;
}

export function canDouble(m, p) {
    const g = m.game;
    if (g.phase !== 'roll' || g.turn !== p || !cubeAvailable(m)) return false;
    if (g.cube.owner !== -1 && g.cube.owner !== p) return false;
    if (g.cube.value >= 64) return false;
    // in a match, doubling a player who already needs fewer points than the
    // cube is worth is pointless for him but legal; we allow it
    return true;
}

export function offerDouble(m, p) {
    if (!canDouble(m, p)) return false;
    const g = m.game;
    g.phase = 'double';
    g.cube.offeredBy = p;
    g.history.push({ p, kind: 'double', value: g.cube.value * 2 });
    return true;
}

export function takeDouble(m) {
    const g = m.game;
    if (g.phase !== 'double') return false;
    const doubler = g.cube.offeredBy, taker = 1 - doubler;
    g.cube.value *= 2;
    g.cube.owner = taker;
    g.cube.offeredBy = -1;
    g.phase = 'roll';
    g.history.push({ p: taker, kind: 'take', value: g.cube.value });
    return true;
}

export function dropDouble(m) {
    const g = m.game;
    if (g.phase !== 'double') return false;
    const doubler = g.cube.offeredBy;
    g.history.push({ p: 1 - doubler, kind: 'drop' });
    finish(m, doubler, g.cube.value, 1, 'drop');
    return true;
}

/** Dice for the player on turn. */
export function roll(m, d1, d2) {
    const g = m.game;
    if (g.phase !== 'roll') return false;
    g.dice = [d1, d2];
    g.phase = 'move';
    tallyRoll(m, g, g.turn, d1, d2);
    return true;
}

/** The legal plays for the dice on the board. */
export function plays(m) {
    const g = m.game;
    if (g.phase !== 'move') return null;
    return R.generatePlays(g.pos, g.turn, g.dice[0], g.dice[1], { firstTurn: firstTurnOf(g, g.turn) });
}

export function turnFor(m) {
    const g = m.game;
    if (g.phase !== 'move') return null;
    return R.createTurn(g.pos, g.turn, g.dice[0], g.dice[1], { firstTurn: firstTurnOf(g, g.turn) });
}

/**
 * Commit a whole turn. The moves are checked against the legal plays — an
 * illegal or incomplete turn is refused (returns false), whoever sent it.
 */
export function commitTurn(m, moves) {
    const g = m.game;
    if (g.phase !== 'move') return false;
    const t = turnFor(m);
    for (const s of moves) {
        if (!t.play({ from: s.from, d: s.d != null ? s.d : inferDie(t, s) })) return false;
    }
    if (!t.done) return false;
    const played = t.played.map((u) => ({ from: u.from, to: u.to, hit: u.hit, d: u.d }));
    const p = g.turn;
    g.pos = R.applyMoves(g.pos, p, played);
    g.history.push({ p, dice: g.dice.slice(), moves: played, note: R.notate(played) });
    g.turnsPlayed[p]++;
    g.dice = null;
    const w = R.winner(g.pos);
    if (w >= 0) {
        const res = R.resultOf(g.pos, w, { koksLong: m.cfg.koksLong });
        finish(m, w, g.cube.value, res.points, res.kind);
        return true;
    }
    g.turn = 1 - p;
    g.phase = 'roll';
    return true;
}

/** A step given only as {from,to}: find the die it takes (a single step). */
function inferDie(t, s) {
    for (const d of new Set(t.rest)) {
        const to = s.from - d <= 0 ? R.OFF : s.from - d;
        if (to === s.to) {
            if (s.to !== R.OFF || s.from - d === 0) return d;
        }
    }
    // bearing off with a higher die: the smallest die that takes it off
    if (s.to === R.OFF) {
        const ds = [...new Set(t.rest)].filter((d) => s.from - d < 0).sort((a, b) => a - b);
        if (ds.length) return ds[0];
    }
    return -1;
}

/** Resign: the opponent wins what the position is worth now (single, mars, koks). */
export function resign(m, p) {
    const g = m.game;
    if (g.phase === 'over') return false;
    const w = 1 - p;
    const res = R.resultOf(g.pos, w, { koksLong: m.cfg.koksLong });
    g.history.push({ p, kind: 'resign' });
    finish(m, w, g.cube.value, res.points, 'resign-' + res.kind);
    return true;
}

function finish(m, w, cube, points, kind) {
    const g = m.game;
    g.phase = 'over';
    g.dice = null;
    const total = cube * points;
    g.result = { winner: w, points: total, base: points, cube, kind };
    m.score[w] += total;
    m.log.push({ winner: w, points: total, kind, cube });
    if (m.cfg.matchTo > 0 && m.score[w] >= m.cfg.matchTo) {
        m.over = true;
        m.winner = w;
    }
}

/** Next game of the match (after «over»). */
export function nextGame(m) {
    if (m.over || m.game.phase !== 'over') return false;
    m.game = newGame(m, m.game.result.winner);
    return true;
}

/** Deep copy through JSON — the state is plain data by design. */
export function serialize(m) {
    return JSON.stringify(m);
}

export function deserialize(s) {
    const m = typeof s === 'string' ? JSON.parse(s) : s;
    if (!m || !m.cfg || !m.game || !m.game.pos || !Array.isArray(m.game.pos.b) || m.game.pos.b.length !== 24) return null;
    if (m.cfg.variant !== R.LONG && m.cfg.variant !== R.SHORT) return null;
    // the checkers must add up — a corrupted save is dropped, not played
    if (R.checkerTotal(m.game.pos, 0) !== 15 || R.checkerTotal(m.game.pos, 1) !== 15) return null;
    return m;
}
